import * as React from "react";
import { usePermissions } from "@/stores";
import { Button, type ButtonProps } from "@/components/ui";

interface PermissionGateProps {
  permission?: string;
  requireAny?: string[];
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

export function PermissionGate({
  permission,
  requireAny,
  fallback = null,
  children,
}: PermissionGateProps) {
  const { hasPermission, hasAnyPermission } = usePermissions();

  const allowed = permission
    ? hasPermission(permission)
    : requireAny && requireAny.length > 0
      ? hasAnyPermission(requireAny)
      : true;

  return <>{allowed ? children : fallback}</>;
}

interface PermissionButtonProps extends ButtonProps {
  permission?: string;
  requireAny?: string[];
  mode?: "hide" | "disable";
  deniedTitle?: string;
}

export const PermissionButton = React.forwardRef<
  HTMLButtonElement,
  PermissionButtonProps
>(function PermissionButton(
  {
    permission,
    requireAny,
    mode = "hide",
    deniedTitle,
    disabled,
    title,
    ...buttonProps
  },
  ref,
) {
  const { hasPermission, hasAnyPermission } = usePermissions();

  const allowed = permission
    ? hasPermission(permission)
    : requireAny && requireAny.length > 0
      ? hasAnyPermission(requireAny)
      : true;

  if (!allowed && mode === "hide") {
    return null;
  }

  const effectiveTitle = !allowed
    ? (deniedTitle ??
      `Requires ${permission ?? requireAny?.join(" or ") ?? "additional"} permission`)
    : title;

  return (
    <Button
      ref={ref}
      disabled={disabled || !allowed}
      title={effectiveTitle}
      {...buttonProps}
    />
  );
});
